import { t } from '@viral-space/shared';

/**
 * Mobile / touch detection + runtime helpers.
 *
 * We treat a device as "mobile" when it reports touch support AND either
 * the UA string looks mobile or the viewport is small. This keeps laptops
 * with touchscreens (e.g. Surface, hybrid ChromeOS) on the desktop layout
 * while still exposing touch controls to iPad/Android browsers.
 */

const TOUCH_UA_PATTERN =
  /android|iphone|ipad|ipod|iemobile|blackberry|opera mini|mobile safari/i;

export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  if (typeof navigator !== 'undefined' && (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints) {
    const mtp = (navigator as unknown as { maxTouchPoints: number }).maxTouchPoints;
    if (mtp > 0) return true;
  }
  return false;
}

export function isLikelyMobile(): boolean {
  if (typeof window === 'undefined') return false;
  // `?mobile=1` (or `?mobile=0`) forces the mobile layout/touch overlay on
  // desktop. Useful for previewing the mobile HUD without an iOS/Android
  // device — e.g. checking that touch buttons don't overlap the desktop
  // chrome on a landscape phone aspect ratio.
  try {
    const p = new URLSearchParams(window.location.search).get('mobile');
    if (p === '1' || p === 'true') return true;
    if (p === '0' || p === 'false') return false;
  } catch {
    // Older browsers / non-standard URLs — fall through to normal detection.
  }
  const ua = navigator.userAgent || '';
  const uaMobile = TOUCH_UA_PATTERN.test(ua);
  const touch = isTouchDevice();
  // iPadOS 13+ reports a desktop UA, so additionally treat any touch device
  // whose viewport is phone/tablet-sized as mobile.
  const smallViewport = Math.min(window.innerWidth, window.innerHeight) <= 820;
  return uaMobile || (touch && smallViewport);
}

export function isPortraitOrientation(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerHeight > window.innerWidth;
}

/**
 * True when the bundle is executing inside the Capacitor (iOS / Android)
 * shell. Capacitor exposes its bridge on `window.Capacitor` before any
 * user code runs, which lets us skip browser-only behaviour (e.g. the
 * Fullscreen API, which the native shell already provides via the
 * fullscreen WebView and the OS-level orientation lock in
 * `Info.plist` / `AndroidManifest.xml`). Calling
 * `Element.requestFullscreen()` inside the iOS WKWebView triggers a
 * grey OS overlay with an X close button that exits fullscreen on tap
 * and visually re-layouts the page — exactly what we don't want when
 * the app is already meant to be fullscreen.
 */
export function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }).Capacitor;
  if (!cap) return false;
  if (typeof cap.isNativePlatform === 'function') {
    try { return cap.isNativePlatform(); } catch { /* fall through */ }
  }
  if (typeof cap.getPlatform === 'function') {
    try {
      const p = cap.getPlatform();
      return p === 'android' || p === 'ios';
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * True when the bundle is executing inside the Capacitor iOS shell
 * specifically (as opposed to Android or the web). Apple's App Store
 * guidelines forbid steering users to external purchase flows for
 * digital goods, and we haven't shipped the Apple in-app purchase
 * integration yet — so the iOS build currently routes Premium upgrades
 * to the web (`https://playzerion.com`) instead of opening Stripe
 * checkout. Keep this distinct from `isCapacitorNative()` so the
 * Android Capacitor build still gets the normal Stripe flow.
 */
export function isCapacitorIOS(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as unknown as {
    Capacitor?: { getPlatform?: () => string };
  }).Capacitor;
  if (!cap || typeof cap.getPlatform !== 'function') return false;
  try {
    return cap.getPlatform() === 'ios';
  } catch {
    return false;
  }
}

/**
 * True when the bundle is running inside the Electron desktop / Steam
 * shell (`packages/desktop`). The preload script there exposes a
 * frozen `window.zerionDesktop` descriptor; we fall back to a UA sniff
 * so the detection still works if a future Electron version disables
 * preload bridges. Used to no-op the browser Fullscreen + Screen
 * Orientation APIs (the desktop window is fullscreened by the main
 * process via `BrowserWindow.setFullScreen(true)`) and to bake the
 * production WebSocket URL into the build at packaging time.
 */
export function isElectronDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as Window).zerionDesktop) return true;
  if (typeof navigator !== 'undefined' && /Electron\//i.test(navigator.userAgent)) return true;
  return false;
}

/**
 * Request the browser enter fullscreen. Safe to call on desktop browsers
 * that don't support it; we just return the promise's rejection silently.
 * Must be called from within a user-gesture handler or the browser will
 * reject it. No-op inside Capacitor native (the WebView is already
 * fullscreen and triggering the JS API there shows a confusing OS-level
 * banner with a close-X that briefly redraws the page on dismiss).
 */
export async function requestFullscreen(target?: HTMLElement): Promise<boolean> {
  if (typeof document === 'undefined') return false;
  if (isCapacitorNative()) return true;
  // Electron's main process drives fullscreen via BrowserWindow.setFullScreen
  // and exposes F11 as the toggle. Calling the browser-side API on top of
  // that produces a flicker as Chromium reports the state change a frame
  // after the native window resize.
  if (isElectronDesktop()) return true;
  const el = target ?? document.documentElement;
  type AnyFs = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
    msRequestFullscreen?: () => Promise<void> | void;
  };
  const anyEl = el as AnyFs;
  try {
    if (document.fullscreenElement) return true;
    if (el.requestFullscreen) {
      await el.requestFullscreen();
      return true;
    }
    if (anyEl.webkitRequestFullscreen) {
      await anyEl.webkitRequestFullscreen();
      return true;
    }
    if (anyEl.msRequestFullscreen) {
      await anyEl.msRequestFullscreen();
      return true;
    }
  } catch {
    // User denied or browser doesn't allow it; keep going without fs.
  }
  return false;
}

export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false;
  return !!document.fullscreenElement;
}

/**
 * Attempt to lock the screen to landscape. Best-effort: Safari and most
 * desktop browsers don't expose the Screen Orientation Lock API, so a
 * failure here is expected and we fall back to the rotate-device overlay.
 * No-op inside Capacitor native — the OS-level orientation lock from
 * `Info.plist` (iOS) / `AndroidManifest.xml` (Android) is already in
 * effect and the JS API would just throw on iOS WKWebView.
 */
export async function lockLandscape(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (isCapacitorNative()) return true;
  // No screen-orientation API in Electron, and the desktop window is
  // always landscape anyway. Pretend the lock succeeded so the mobile
  // overlay's "tap to begin" gesture short-circuits cleanly if the
  // desktop bundle is ever loaded with ?mobile=1.
  if (isElectronDesktop()) return true;
  const orientation = (screen as unknown as {
    orientation?: { lock?: (o: string) => Promise<void> };
  }).orientation;
  if (orientation && typeof orientation.lock === 'function') {
    try {
      await orientation.lock('landscape');
      return true;
    } catch {
      // Ignored — handled by the rotate overlay.
    }
  }
  return false;
}

/**
 * Full-screen "TAP TO BEGIN" gate for mobile devices. It does two jobs:
 *
 *   1. Gives us a user-gesture anchor inside which we can call
 *      `requestFullscreen()` and `screen.orientation.lock('landscape')`
 *      (both APIs require an explicit user gesture on most browsers).
 *   2. Shows a rotate-device card when the viewport is portrait, and
 *      replaces itself with the game once the viewport is landscape and
 *      the user has tapped.
 *
 * The overlay also re-appears automatically when the device leaves
 * landscape later (rotation back to portrait mid-game).
 */
export class MobileStartupOverlay {
  private container: HTMLElement;
  private root: HTMLElement;
  private rotateCard!: HTMLElement;
  private tapCard!: HTMLElement;
  /**
   * Logo splash card shown for ~1.5s before the tap card appears on
   * the very first session. Repeat opens (sign-out → re-show) skip
   * the splash so returning players don't sit through the animation
   * every time. Hidden after the splash finishes.
   */
  private splashCard!: HTMLElement;
  private bgShip!: HTMLElement;
  private splashTimer: number | null = null;
  /**
   * Whether the splash has played at least once this page load. Stops
   * `reset()` (post-signout re-show) from re-running the cinematic.
   */
  private splashPlayed = false;
  private onReady: () => void;
  private disposed = false;
  private active = true;

  private readonly onOrientationChange: () => void;
  private readonly onFsChange: () => void;

  constructor(container: HTMLElement, onReady: () => void) {
    this.container = container;
    this.onReady = onReady;

    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed',
      inset: '0',
      background: '#000008',
      color: '#00ff41',
      fontFamily: "'Share Tech Mono', monospace",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '24px',
      zIndex: '10000',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      userSelect: 'none',
      webkitUserSelect: 'none',
      touchAction: 'manipulation',
      overflow: 'hidden',
    } as Partial<CSSStyleDeclaration>);
    this.container.appendChild(this.root);

    this.injectKeyframes();
    this.buildBackgroundShip();
    this.buildSplashCard();
    this.buildTapCard();
    this.buildRotateCard();

    this.onOrientationChange = () => this.refresh();
    this.onFsChange = () => {
      // If the user exited fullscreen after starting the game, we prefer
      // to leave them alone (browsers often exit FS when a chrome menu
      // opens); we only re-show the rotate card when rotation becomes
      // portrait — that's handled in refresh().
      this.refresh();
    };
    window.addEventListener('resize', this.onOrientationChange);
    window.addEventListener('orientationchange', this.onOrientationChange);
    document.addEventListener('fullscreenchange', this.onFsChange);

    this.start();
  }

  /**
   * Inject the keyframes used by the splash, the rotate-device hint,
   * the tap-to-begin button, and the background ship drift. Idempotent
   * via a stable element id so a second overlay (e.g. after sign-out
   * + re-show) doesn't double up the styles.
   */
  private injectKeyframes(): void {
    if (document.getElementById('vs-mobile-kf')) return;
    const style = document.createElement('style');
    style.id = 'vs-mobile-kf';
    style.textContent = `
      @keyframes vs-rotate-hint {
        0%   { transform: rotate(-10deg); }
        50%  { transform: rotate(80deg); }
        100% { transform: rotate(-10deg); }
      }
      @keyframes vs-pulse {
        0%, 100% { box-shadow: 0 0 8px #00ff41, 0 0 16px rgba(0,255,65,0.3); }
        50% { box-shadow: 0 0 16px #00ff41, 0 0 32px rgba(0,255,65,0.5), 0 0 48px rgba(0,255,65,0.2); }
      }
      /*
       * Splash logo: ramp opacity from 0→1, hold visible, then fade
       * back to ~0 for the cross-fade into the tap card. Combined
       * with a slight scale to give the logo a "warming up" feel.
       */
      @keyframes vs-splash {
        0%   { opacity: 0; transform: scale(0.92); }
        20%  { opacity: 1; transform: scale(1); }
        70%  { opacity: 1; transform: scale(1.02); }
        100% { opacity: 0; transform: scale(1.05); }
      }
      /*
       * Tap card cross-fade. Triggered by toggling the
       * \`vs-cards-in\` class on each card after the splash ends so
       * the entrance feels intentional rather than a hard cut.
       */
      @keyframes vs-card-in {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      /*
       * Background hero ship: drift slowly across the viewport,
       * left to right and slightly down, then loop. Long duration
       * (~50s) keeps the motion subliminal — players read it as
       * ambient parallax, not a dancing decoration.
       */
      @keyframes vs-bg-ship-drift {
        0%   { transform: translate(-30vw, -6vh) rotate(-4deg); opacity: 0; }
        12%  { opacity: 0.18; }
        50%  { opacity: 0.22; }
        88%  { opacity: 0.15; }
        100% { transform: translate(70vw, 4vh) rotate(-4deg); opacity: 0; }
      }
      /*
       * Subtle starfield twinkle for the ambient backdrop. Two
       * staggered gradient layers drift at different speeds so the
       * scene reads "deep space" without us having to script per-
       * pixel star positions.
       */
      @keyframes vs-bg-stars-near {
        from { background-position: 0 0; }
        to   { background-position: 200px 80px; }
      }
      @keyframes vs-bg-stars-far {
        from { background-position: 0 0; }
        to   { background-position: -120px 50px; }
      }
    `;
    document.head.appendChild(style);
  }

  private buildBackgroundShip(): void {
    // Layered backdrop:
    //   1. Two radial-gradient star layers drifting at different
    //      speeds for a parallax "deep space" feel.
    //   2. A large hero ship inline-SVG drifting across the viewport
    //      with subtle opacity + green glow so it reads as ambient
    //      atmosphere instead of foreground content.
    const stars = document.createElement('div');
    Object.assign(stars.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '0',
      backgroundImage: `
        radial-gradient(1px 1px at 20% 30%, rgba(180,255,200,0.55), transparent 60%),
        radial-gradient(1px 1px at 70% 60%, rgba(0,255,65,0.4), transparent 60%),
        radial-gradient(1.5px 1.5px at 40% 80%, rgba(180,255,200,0.5), transparent 60%),
        radial-gradient(1px 1px at 85% 20%, rgba(0,255,65,0.55), transparent 60%),
        radial-gradient(1.5px 1.5px at 10% 70%, rgba(180,255,200,0.45), transparent 60%),
        radial-gradient(1px 1px at 55% 15%, rgba(180,255,200,0.4), transparent 60%)
      `,
      backgroundSize: '420px 280px, 320px 240px, 540px 360px, 280px 200px, 360px 240px, 240px 180px',
      animation: 'vs-bg-stars-far 60s linear infinite',
      opacity: '0.6',
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(stars);

    this.bgShip = document.createElement('div');
    Object.assign(this.bgShip.style, {
      position: 'absolute',
      top: '38%',
      left: '0',
      width: 'min(60vw, 480px)',
      pointerEvents: 'none',
      zIndex: '0',
      opacity: '0',
      // Cubic-bezier rather than linear so the ship eases into and
      // out of view at the edges instead of snapping; combined with
      // the opacity ramp in the keyframes that gives the impression
      // the ship is "fading through" the scene.
      animation: 'vs-bg-ship-drift 52s cubic-bezier(.45,.05,.55,.95) infinite',
      filter: 'drop-shadow(0 0 10px rgba(0,255,65,0.35)) drop-shadow(0 0 28px rgba(0,255,65,0.18))',
      mixBlendMode: 'screen',
    } as Partial<CSSStyleDeclaration>);
    this.bgShip.innerHTML = `
      <svg viewBox="0 0 480 160" width="100%" height="auto"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <linearGradient id="vsHull" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%"  stop-color="#5ce67d" stop-opacity="0.85"/>
            <stop offset="50%" stop-color="#1a8c33" stop-opacity="0.9"/>
            <stop offset="100%" stop-color="#0a3a14" stop-opacity="0.95"/>
          </linearGradient>
          <radialGradient id="vsEngine" cx="0" cy="0.5" r="1">
            <stop offset="0%"  stop-color="#aaffaa" stop-opacity="0.95"/>
            <stop offset="60%" stop-color="#00ff41" stop-opacity="0.5"/>
            <stop offset="100%" stop-color="#00ff41" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <!-- Engine plume trailing the ship -->
        <ellipse cx="60"  cy="80" rx="60" ry="9"  fill="url(#vsEngine)" opacity="0.85"/>
        <ellipse cx="40"  cy="80" rx="38" ry="5"  fill="url(#vsEngine)" opacity="0.95"/>
        <!-- Main hull silhouette: long fuselage + swept wings -->
        <path d="M 110 80
                 L 160 64
                 L 320 60
                 L 410 78
                 L 430 80
                 L 410 82
                 L 320 100
                 L 160 96 Z"
              fill="url(#vsHull)" stroke="#9bff9b" stroke-width="0.6" opacity="0.95"/>
        <!-- Upper bridge / cockpit silhouette -->
        <path d="M 230 60 L 285 50 L 330 56 L 345 66 L 230 70 Z"
              fill="#0a2a14" stroke="#7fff7f" stroke-width="0.6" opacity="0.85"/>
        <!-- Lower wing fin -->
        <path d="M 200 96 L 280 110 L 330 108 L 280 100 Z"
              fill="#0a3a14" stroke="#5fbf5f" stroke-width="0.5" opacity="0.85"/>
        <!-- Forward sensor / nose lights -->
        <circle cx="425" cy="80" r="2.2" fill="#aaffaa" opacity="0.9"/>
        <circle cx="408" cy="76" r="1.4" fill="#aaffaa" opacity="0.7"/>
        <circle cx="408" cy="84" r="1.4" fill="#aaffaa" opacity="0.7"/>
      </svg>
    `;
    this.root.appendChild(this.bgShip);
  }

  private buildSplashCard(): void {
    this.splashCard = document.createElement('div');
    Object.assign(this.splashCard.style, {
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: '2',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);

    const logo = document.createElement('img');
    logo.src = `${import.meta.env.BASE_URL}images/zerion-logo.png`;
    logo.alt = 'Zerion: Infinite Space';
    Object.assign(logo.style, {
      width: 'min(70vw, 60vh, 420px)',
      height: 'auto',
      filter:
        'drop-shadow(0 0 18px rgba(0,255,65,0.55)) drop-shadow(0 0 48px rgba(0,255,65,0.25))',
    } as Partial<CSSStyleDeclaration>);
    this.splashCard.appendChild(logo);

    this.root.appendChild(this.splashCard);
  }

  private buildTapCard(): void {
    this.tapCard = document.createElement('div');
    Object.assign(this.tapCard.style, {
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '18px',
      maxWidth: '560px',
      position: 'relative',
      zIndex: '2',
    } as Partial<CSSStyleDeclaration>);

    // Two-line title: "ZERION" big, "INFINITE SPACE" smaller below,
    // with a thin animated bar separating them. The previous one-line
    // layout wrapped on iPhone landscape because the 8px letter-spacing
    // pushed the string past the safe-area horizontal width.
    const titleWrap = document.createElement('div');
    Object.assign(titleWrap.style, {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
    } as Partial<CSSStyleDeclaration>);

    const titleMain = document.createElement('div');
    Object.assign(titleMain.style, {
      // Bigger than the previous title since it now stands alone on
      // its own line. Capped on huge displays so it doesn't dwarf the
      // rest of the card.
      fontSize: 'clamp(40px, 11vw, 84px)',
      fontWeight: '700',
      letterSpacing: 'clamp(6px, 1.6vw, 14px)',
      lineHeight: '1',
      textShadow:
        '0 0 12px #00ff41, 0 0 36px rgba(0,255,65,0.55), 0 0 64px rgba(0,255,65,0.25)',
    } as Partial<CSSStyleDeclaration>);
    titleMain.textContent = 'ZERION';
    titleWrap.appendChild(titleMain);

    const divider = document.createElement('div');
    Object.assign(divider.style, {
      width: 'clamp(80px, 18vw, 160px)',
      height: '1px',
      background:
        'linear-gradient(90deg, transparent 0%, #00ff41 50%, transparent 100%)',
      margin: '6px 0',
      opacity: '0.7',
    } as Partial<CSSStyleDeclaration>);
    titleWrap.appendChild(divider);

    const titleSub = document.createElement('div');
    Object.assign(titleSub.style, {
      fontSize: 'clamp(13px, 3.2vw, 20px)',
      letterSpacing: 'clamp(4px, 1.2vw, 10px)',
      color: '#7fff9c',
      textShadow: '0 0 6px rgba(0,255,65,0.7)',
    } as Partial<CSSStyleDeclaration>);
    titleSub.textContent = 'INFINITE SPACE';
    titleWrap.appendChild(titleSub);

    this.tapCard.appendChild(titleWrap);

    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: 'clamp(10px, 2.4vw, 13px)',
      opacity: '0.78',
      lineHeight: '1.6',
      textTransform: 'none',
      letterSpacing: '0.5px',
      maxWidth: '520px',
      textAlign: 'center',
    } as Partial<CSSStyleDeclaration>);
    subtitle.textContent = t('mobile.intro');
    this.tapCard.appendChild(subtitle);

    const button = document.createElement('button');
    Object.assign(button.style, {
      marginTop: '4px',
      // Reserve enough space below the button so the absolutely-
      // positioned copyright (anchored to the viewport bottom) doesn't
      // visually crash into the button's glow on short landscape
      // viewports like iPhone 12 Pro (~390px tall).
      marginBottom: 'clamp(28px, 6vh, 56px)',
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: 'clamp(13px, 3vw, 16px)',
      padding: '12px 36px',
      background: 'transparent',
      border: '2px solid #00ff41',
      color: '#00ff41',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      boxShadow: '0 0 12px rgba(0,255,65,0.4), inset 0 0 12px rgba(0,255,65,0.08)',
      animation: 'vs-pulse 2s ease-in-out infinite',
      touchAction: 'manipulation',
      pointerEvents: 'auto',
    } as Partial<CSSStyleDeclaration>);
    button.textContent = t('mobile.tapToBegin');

    const handler = async (e: Event) => {
      e.preventDefault();
      // Kick off fullscreen + orientation lock from inside the gesture.
      // We await the fullscreen promise first because some browsers (iOS
      // Safari) return from requestFullscreen before the orientation API
      // will accept a lock call.
      await requestFullscreen(document.documentElement);
      await lockLandscape();
      this.proceed();
    };
    button.addEventListener('click', handler);
    button.addEventListener('touchend', handler);
    this.tapCard.appendChild(button);

    this.root.appendChild(this.tapCard);
    this.buildCopyright();
  }

  /**
   * Run the one-shot logo splash. Resolves once the splash is hidden
   * and the tap/rotate cards are free to take over. If the splash has
   * already played this page load (e.g. user signed out and we're
   * re-showing the overlay) this is a no-op.
   */
  private playSplashOnce(): void {
    if (this.splashPlayed || this.disposed) return;
    this.splashPlayed = true;
    this.tapCard.style.display = 'none';
    this.rotateCard.style.display = 'none';
    this.splashCard.style.display = 'flex';
    this.splashCard.style.animation = 'vs-splash 1700ms ease-in-out forwards';

    this.splashTimer = window.setTimeout(() => {
      this.splashTimer = null;
      this.splashCard.style.display = 'none';
      // Ramp the cards in via a CSS transition rather than swapping
      // display directly, so the user sees a smooth hand-off from the
      // logo into either the tap card or the rotate card.
      this.refresh();
      const card =
        this.rotateCard.style.display !== 'none' ? this.rotateCard : this.tapCard;
      card.style.animation = 'vs-card-in 360ms ease-out forwards';
    }, 1700);
  }

  private buildCopyright(): void {
    const year = new Date().getFullYear();
    const credit = document.createElement('div');
    Object.assign(credit.style, {
      position: 'absolute',
      left: '0',
      right: '0',
      bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
      textAlign: 'center',
      fontSize: 'clamp(9px, 1.7vw, 10px)',
      opacity: '0.5',
      letterSpacing: '2px',
      textTransform: 'none',
      color: '#00ff41',
      pointerEvents: 'none',
      zIndex: '2',
    } as Partial<CSSStyleDeclaration>);
    credit.textContent = `\u00A9 ${year} Gilberto Avalos`;
    this.root.appendChild(credit);
  }

  private buildRotateCard(): void {
    this.rotateCard = document.createElement('div');
    Object.assign(this.rotateCard.style, {
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '18px',
      maxWidth: '520px',
      position: 'relative',
      zIndex: '2',
    } as Partial<CSSStyleDeclaration>);

    const icon = document.createElement('div');
    Object.assign(icon.style, {
      fontSize: '64px',
      animation: 'vs-rotate-hint 2s ease-in-out infinite',
      display: 'inline-block',
    });
    icon.textContent = '↻';
    this.rotateCard.appendChild(icon);

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: 'clamp(16px, 4vw, 22px)',
      letterSpacing: '4px',
      textShadow: '0 0 8px #00ff41',
    });
    title.textContent = t('mobile.rotateTitle');
    this.rotateCard.appendChild(title);

    const msg = document.createElement('div');
    Object.assign(msg.style, {
      fontSize: 'clamp(10px, 2.5vw, 12px)',
      opacity: '0.7',
      lineHeight: '1.6',
      textTransform: 'none',
      letterSpacing: '1px',
    });
    msg.textContent = t('mobile.rotateBody');
    this.rotateCard.appendChild(msg);

    this.root.appendChild(this.rotateCard);
  }

  private refresh(): void {
    if (this.disposed) return;
    // While the splash is still on screen we don't surface either of the
    // two action cards — the splash card holds focus and `playSplashOnce`
    // unhides them on completion.
    if (this.splashCard.style.display !== 'none' && this.active) {
      this.root.style.display = 'flex';
      return;
    }
    const portrait = isPortraitOrientation();
    if (this.active) {
      // Pre-start: show tap card when landscape, rotate card when portrait.
      this.tapCard.style.display = portrait ? 'none' : 'flex';
      this.rotateCard.style.display = portrait ? 'flex' : 'none';
      this.root.style.display = 'flex';
      return;
    }
    // Post-start: overlay only reappears to warn about a portrait flip.
    if (portrait) {
      this.tapCard.style.display = 'none';
      this.rotateCard.style.display = 'flex';
      this.root.style.display = 'flex';
    } else {
      this.root.style.display = 'none';
    }
  }

  /**
   * Public entry the constructor triggers automatically and `reset()`
   * re-uses. Plays the logo splash on first run, then hands off to the
   * tap / rotate cards.
   */
  private start(): void {
    if (!this.splashPlayed) {
      this.playSplashOnce();
    } else {
      this.refresh();
    }
  }

  private proceed(): void {
    this.active = false;
    this.tapCard.style.display = 'none';
    this.refresh();
    this.onReady();
  }

  /** Re-show the TAP-TO-BEGIN overlay (e.g. after explicit sign-out). */
  reset(): void {
    this.active = true;
    this.refresh();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.splashTimer !== null) {
      clearTimeout(this.splashTimer);
      this.splashTimer = null;
    }
    window.removeEventListener('resize', this.onOrientationChange);
    window.removeEventListener('orientationchange', this.onOrientationChange);
    document.removeEventListener('fullscreenchange', this.onFsChange);
    this.root.remove();
  }
}
